import {
  Activity,
  CheckCircle,
  XCircle,
  TrendingUp,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { MigrationSummary } from '@/types';

interface DashboardStatsProps {
  migrations: MigrationSummary[];
  className?: string | undefined;
}

interface StatCardProps {
  title: string;
  value: number | string;
  description?: string | undefined;
  icon: typeof Activity;
  iconColor?: string | undefined;
  trend?: { value: number; label: string } | undefined;
}

function StatCard({
  title,
  value,
  description,
  icon: Icon,
  iconColor = 'text-muted-foreground',
  trend,
}: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={cn('h-4 w-4', iconColor)} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
        {trend && (
          <div className="flex items-center gap-1 mt-1">
            <TrendingUp
              className={cn(
                'h-3 w-3',
                trend.value >= 0 ? 'text-green-600' : 'text-destructive'
              )}
            />
            <span
              className={cn(
                'text-xs',
                trend.value >= 0 ? 'text-green-600' : 'text-destructive'
              )}
            >
              {trend.value >= 0 ? '+' : ''}
              {trend.value}% {trend.label}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function DashboardStats({ migrations, className }: DashboardStatsProps) {
  // Calculate statistics
  const totalMigrations = migrations.length;
  const inProgressCount = migrations.filter((m) => m.status === 'InProgress').length;
  const succeededCount = migrations.filter((m) => m.status === 'Succeeded').length;
  const failedCount = migrations.filter((m) => m.status === 'Failed').length;
  const partialCount = migrations.filter((m) => m.status === 'Partial').length;

  // Calculate success rate
  const completedMigrations = succeededCount + failedCount + partialCount;
  const successRate =
    completedMigrations > 0
      ? Math.round((succeededCount / completedMigrations) * 100)
      : 0;

  // Calculate average duration for completed migrations
  const completedWithDuration = migrations.filter(
    (m) => m.status !== 'InProgress' && m.completedAt
  );
  const averageDuration =
    completedWithDuration.length > 0
      ? completedWithDuration.reduce((acc, m) => {
          if (!m.completedAt) return acc;
          return (
            acc +
            (new Date(m.completedAt).getTime() - new Date(m.startedAt).getTime())
          );
        }, 0) / completedWithDuration.length
      : 0;

  const formatDuration = (ms: number): string => {
    if (ms === 0) return '—';
    const minutes = Math.floor(ms / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  return (
    <div className={cn('grid gap-4 md:grid-cols-2 lg:grid-cols-4', className)}>
      <StatCard
        title="Total Migrations"
        value={totalMigrations}
        description={`${inProgressCount} in progress`}
        icon={Activity}
        iconColor="text-primary"
      />
      <StatCard
        title="Succeeded"
        value={succeededCount}
        description={`${successRate}% success rate`}
        icon={CheckCircle}
        iconColor="text-green-600"
      />
      <StatCard
        title="Failed"
        value={failedCount}
        description={partialCount > 0 ? `${partialCount} partial` : undefined}
        icon={XCircle}
        iconColor="text-destructive"
      />
      <StatCard
        title="Avg Duration"
        value={formatDuration(averageDuration)}
        description="for completed migrations"
        icon={Clock}
        iconColor="text-muted-foreground"
      />
    </div>
  );
}
